import { type FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';

import { PROMO_CODE_TRADE_FIRST_WELCOME } from './helpers/expiringPromoMessageHelpers';

const BONUS_POINTS_AMOUNT = 5000;

import { type ExpiringPromoMessage_promotion$data } from './__generated__/ExpiringPromoMessage_promotion.graphql';

type Props = {
    promotion: ExpiringPromoMessage_promotion$data;
    isLowResolution: boolean;
    currency: string;
};

const ExpiringPromoMessage: FC<Props> = ({ promotion, isLowResolution, currency }) => {
    const { code, daysRelativeToExpiration, percentAdjustmentRate, flatAdjustmentRate } =
        promotion || {};
    const daysToExpiration = Math.ceil(Number(daysRelativeToExpiration));
    const percentRate = percentAdjustmentRate?.rate;

    const bonusPoints = <FormattedNumber value={BONUS_POINTS_AMOUNT} />;
    const daysRemaining = (
        <FormattedMessage
            id="dbl.Header.MessageBanner.daysRemaining"
            defaultMessage="{daysToExpiration, plural, one {# day} other {# days}}"
            values={{ daysToExpiration }}
        />
    );

    let message = null;
    let messageMobile = null;

    if (code === PROMO_CODE_TRADE_FIRST_WELCOME) {
        message = (
            <FormattedMessage
                id="dbl.Header.MessageBanner.bonusPointsExpiration"
                defaultMessage="Only {daysRemaining} left to earn {bonusPoints} bonus points on your purchase with code {code}"
                values={{
                    daysRemaining,
                    bonusPoints,
                    code,
                }}
            />
        );
        messageMobile = (
            <FormattedMessage
                id="dbl.Header.MessageBanner.bonusPointsExpirationMobile"
                defaultMessage="{daysRemaining} left to earn {bonusPoints} bonus points with code {code}"
                values={{
                    daysRemaining,
                    bonusPoints,
                    code,
                }}
            />
        );
    } else if (percentRate) {
        const percentRateNum = <FormattedNumber value={percentRate / 100} style="percent" />;
        message = (
            <FormattedMessage
                id="dbl.Header.MessageBanner.percentDiscountTypeMessage"
                defaultMessage="Only {daysRemaining} left to save {percentRateNum} on your purchase with code {code}"
                values={{
                    daysRemaining,
                    percentRateNum,
                    code,
                }}
            />
        );
        messageMobile = (
            <FormattedMessage
                id="dbl.Header.MessageBanner.percentDiscountTypeMessageMobile"
                defaultMessage="Only {daysRemaining} left to save {percentRateNum} with code {code}"
                values={{
                    daysRemaining,
                    percentRateNum,
                    code,
                }}
            />
        );
    } else {
        const flatRateAmountList = flatAdjustmentRate?.rate?.convertedAmountList || [];
        const flatRateAmount = flatRateAmountList.find(c => c?.currency === currency);
        const flatRate = (
            <FormattedNumber
                value={flatRateAmount?.amount || 0}
                style="currency"
                minimumFractionDigits={0}
                maximumFractionDigits={0}
                currency={currency}
            />
        );
        message = (
            <FormattedMessage
                id="dbl.Header.MessageBanner.flatDiscountTypeMessage"
                defaultMessage="Only {daysRemaining} left to save {flatRate} on your purchase with code {code}"
                values={{
                    daysRemaining,
                    flatRate,
                    code,
                }}
            />
        );
        messageMobile = (
            <FormattedMessage
                id="dbl.Header.MessageBanner.flatDiscountTypeMessageMobile"
                defaultMessage="Only {daysRemaining} left to save {flatRate} with code {code}"
                values={{
                    daysRemaining,
                    flatRate,
                    code,
                }}
            />
        );
    }

    return isLowResolution ? messageMobile : message;
};

export default createFragmentContainer(ExpiringPromoMessage, {
    promotion: graphql`
        fragment ExpiringPromoMessage_promotion on Promotion {
            code
            daysRelativeToExpiration(truncate: false)
            promotionMeta {
                urgencyMessagingDisplay
                urgencyMessagingDaysBeforeExpiration
            }
            flatAdjustmentRate {
                rate {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
            percentAdjustmentRate {
                rate
            }
        }
    `,
});
